/**
 * This file is part of the Haiman.
 * @link     : https://haiman.io/
 * @author   : Emil Zhai (root@derzh.com)
 * @modifier : Emil Zhai (root@derzh.com)
 * @copyright: Copyright (c) 2018 Hangzhou Haila Network Technology Co., Ltd.
 */

import store from '@/store';
import * as api from '@/api/secret';
import { SecretComment } from '@/api/types/secret';
import { SECRET } from '@/store/types';
import { StoreDataCache, findCache, saveCache } from '@/store/cache';
import { finalizeAction, StoreActionEnv } from '@/store/actions';

export interface StoreSecretCommentInfo {
  filter: string;
  kw: string;
  lock: boolean;
  list: SecretComment[];
  limit: number;
  point: number | undefined;
  height: number;
  noMore: boolean;
  extra: {
    partial: boolean;
    last: number;
  };
  updateTime: number;
}

const newCommentInfo = (filter: string, kw: string): StoreSecretCommentInfo => ({
  lock: false,
  list: [],
  filter,
  kw,
  limit: 10,
  point: void 0,
  height: 0,
  noMore: false,
  extra: {
    partial: false,
    last: 0,
  },
  updateTime: 0,
});

export interface StoreSecretCommentFilterState {
  cache: StoreDataCache<StoreSecretCommentInfo>[];
  maxCache: number;
}

export default {
  namespaced: true,
  state: {
    cache: [],
    maxCache: 20,
  },
  getters: {},
  actions: {
    [SECRET.GET_COMMENT](
      { commit, state }: StoreActionEnv<StoreSecretCommentFilterState>,
      { action: rawAction, force, filter = '', kw, silent },
    ) {
      let cache = findCache(state.cache, { filter, kw });
      const action = finalizeAction(rawAction, cache, cache && cache.noMore && !force);
      if (!cache) {
        commit(SECRET.GET_COMMENT, { status: 'new', filter, kw });
        cache = findCache(state.cache, { filter, kw });
      }
      if (action && cache) {
        const limit = cache.limit;
        const point = action === 'reload' ? void 0 : cache.point;
        commit(SECRET.GET_COMMENT, { status: 'start', filter, kw });
        return new Promise((resolve, reject) => {
          Promise.all([
            api.getComments({ filter, point, limit, kw, silent }),
            store.dispatch(`secret/${SECRET.GET_EMOTIONS}`),
            // store.dispatch(`secret/topic/list/${SECRET.GET_TOPIC_LIST}`),
          ]).then(([res]) => {
            commit(SECRET.GET_COMMENT, {
              status: 'success',
              action,
              filter,
              kw,
              point,
              list: res.data,
              extra: res.extra,
            });
            resolve();
          }).catch(() => {
            commit(SECRET.GET_COMMENT, { status: 'failure', filter, kw });
            reject();
          });
        });
      }
      return Promise.resolve();
    },
  },
  mutations: {
    [SECRET.GET_COMMENT](state: StoreSecretCommentFilterState, { status, action, filter, kw, point, list, extra }) {
      if (status === 'new') {
        const cache = newCommentInfo(filter, kw);
        saveCache(state.cache, { filter, kw }, cache, state.maxCache);
      } else if (status === 'start') {
        const cache = findCache(state.cache, { filter, kw });
        if (cache) {
          cache.lock = true;
        }
      } else if (status === 'success') {
        const cache = findCache(state.cache, { filter, kw }) || newCommentInfo(filter, kw);
        if (!point) {
          cache.extra.last = extra.last;
          cache.extra.partial = extra.partial;
        }
        cache.list = action === 'reload' ? list : cache.list.concat(list);
        cache.noMore = list.length < cache.limit;
        cache.point = cache.list.length === 0 ? void 0 : cache.list[cache.list.length - 1].id;
        cache.filter = filter;
        cache.kw = kw;
        cache.updateTime = new Date().valueOf();
        cache.lock = false;
        saveCache(state.cache, { filter, kw }, cache, state.maxCache);
      }
    },
    [SECRET.COMMENT_DEL_SUCCESS](state: StoreSecretCommentFilterState, id) {
      state.cache.forEach((item) => {
        const index = item.data.list.findIndex(p => p.id === id);
        if (index >= 0) {
          item.data.list.splice(index, 1);
        }
      });
    },
  },
};
